import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, FormControl, InputLabel, Select, MenuItem, FormHelperText, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from '../config/axios';
import { useAuth } from '../contexts/AuthContext';
import ReportIcon from '@mui/icons-material/Report';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';

const ReportIconContainer = styled(Box)(({ theme, showTooltip }) => ({
  position: 'fixed',
  bottom: '580px', // Moved higher to avoid overlap
  right: '20px', // Back to original position
  zIndex: showTooltip ? 9990 : 9998, // Lower z-index when tooltip is shown
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
}));

const ReportIconOverlay = styled(Box)(({ theme }) => ({
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)', // Red gradient
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease-in-out',
  boxShadow: '0 4px 20px rgba(244, 67, 54, 0.3)',
  border: '2px solid rgba(255, 255, 255, 0.2)',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 6px 25px rgba(244, 67, 54, 0.5)',
  },
}));

const ReportTooltip = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: '10px',
  right: '80px',
  width: '280px',
  background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)', // Red gradient
  border: '2px solid #f44336', // Red border
  borderRadius: '12px',
  padding: theme.spacing(2),
  zIndex: 10014,
  boxShadow: '0 12px 40px rgba(244, 67, 54, 0.4)',
  backdropFilter: 'blur(10px)',
  animation: 'tooltipSlideIn 0.3s ease-out',
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: '20px',
    right: '-10px',
    width: '0',
    height: '0',
    borderTop: '10px solid transparent',
    borderBottom: '10px solid transparent',
    borderLeft: '10px solid #d32f2f', // Red arrow pointing right
  },
}));

const ReportIconComponent = () => {
  const { user } = useAuth();
  const [showTooltip, setShowTooltip] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportType, setReportType] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [files, setFiles] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [consentDialogOpen, setConsentDialogOpen] = useState(false);
  
  // Validation states
  const [errors, setErrors] = useState({
    reportType: '',
    description: '',
    url: '',
    files: ''
  });
  const [touched, setTouched] = useState({
    reportType: false,
    description: false,
    url: false,
    files: false
  });

  const reportTypes = [
    { value: 'fake_webshop', label: 'Fake Webshop', description: 'Betrügerische Online-Shops' },
    { value: 'phishing', label: 'Phishing', description: 'Datendiebstahl-Angriffe' },
    { value: 'malware', label: 'Malware', description: 'Schadsoftware oder Viren' },
    { value: 'scam', label: 'Betrug', description: 'Betrügerische Angebote' },
    { value: 'other', label: 'Sonstiges', description: 'Andere verdächtige Aktivitäten' }
  ];

  // Validation functions
  const validateReportType = (value) => {
    if (!value || !value.trim()) {
      return 'Berichtstyp ist erforderlich';
    }
    const allowedTypes = ['fake_webshop', 'phishing', 'malware', 'scam', 'other'];
    if (!allowedTypes.includes(value)) {
      return 'Ungültiger Berichtstyp';
    }
    return '';
  };

  const validateDescription = (value) => {
    if (!value || !value.trim()) {
      return 'Beschreibung ist erforderlich';
    }
    if (value.length > 2000) {
      return 'Beschreibung darf maximal 2000 Zeichen haben';
    }
    // Check for suspicious patterns (HTML/JS)
    const suspiciousPatterns = [
      /<script[^>]*>.*?<\/script>/gi,
      /javascript:/gi,
      /on\w+\s*=/gi,
      /<iframe[^>]*>/gi,
      /<object[^>]*>/gi,
      /<embed[^>]*>/gi,
      /<link[^>]*>/gi,
      /<meta[^>]*>/gi,
      /<style[^>]*>.*?<\/style>/gi
    ];
    for (const pattern of suspiciousPatterns) {
      if (pattern.test(value)) {
        return 'Beschreibung enthält nicht erlaubte Inhalte (HTML/JavaScript)';
      }
    }
    return '';
  };

  const validateUrl = (value) => {
    if (!value || !value.trim()) {
      return '';
    }
    const trimmedValue = value.trim();
    if (trimmedValue.length > 500) {
      return 'URL darf maximal 500 Zeichen haben';
    }
    const urlPattern = /^https?:\/\/[^\s/$.?#].[^\s]*$/;
    if (!urlPattern.test(trimmedValue)) {
      return 'URL muss mit http:// oder https:// beginnen und gültig sein';
    }
    return '';
  };

  const validateFiles = (fileList) => {
    if (fileList.length > 3) {
      return 'Maximal 3 Dateien erlaubt';
    }
    for (const file of fileList) {
      if (file.size > 10 * 1024 * 1024) { // 10MB
        return `Datei "${file.name}" ist zu groß (max. 10MB)`;
      }
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        return `Datei "${file.name}" hat ein nicht unterstütztes Format`;
      }
    }
    return '';
  };

  // Update validation when values change
  const updateValidation = (field, value) => {
    let error = '';
    switch (field) {
      case 'reportType':
        error = validateReportType(value);
        break;
      case 'description':
        error = validateDescription(value);
        break;
      case 'url':
        error = validateUrl(value);
        break;
      case 'files':
        error = validateFiles(value);
        break;
    }
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleReportSubmit = () => {
    // Mark all fields as touched to show validation errors
    setTouched({
      reportType: true,
      description: true,
      url: true,
      files: true
    });

    // Validate all fields
    const reportTypeError = validateReportType(reportType);
    const descriptionError = validateDescription(description);
    const urlError = validateUrl(url);
    const filesError = validateFiles(files);

    setErrors({
      reportType: reportTypeError,
      description: descriptionError,
      url: urlError,
      files: filesError
    });

    // Check if there are any errors
    if (reportTypeError || descriptionError || urlError || filesError) {
      setSnackbar({
        open: true,
        message: 'Bitte korrigiere die Fehler im Formular.',
        severity: 'error'
      });
      return;
    }

    // Show consent dialog first
    setConsentDialogOpen(true);
  };

  const handleConsentConfirm = async () => {
    setConsentDialogOpen(false);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('report_type', reportType);
      formData.append('description', description.trim());
      if (url.trim()) {
        formData.append('url', url.trim());
      }
      
      // Add files
      files.forEach((file) => {
        formData.append('files', file);
      });

      const response = await axios.post('/reports/submit', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSnackbar({
        open: true,
        message: response.data.message || 'Bericht erfolgreich eingereicht!',
        severity: 'success'
      });

      // Reset form
      setReportType('');
      setDescription('');
      setUrl('');
      setFiles([]);
      setShowReportDialog(false);

    } catch (error) {
      console.error('Error submitting report:', error);
      
      let errorMessage = 'Fehler beim Einreichen des Berichts. Bitte versuche es erneut.';
      
      if (error.response?.status === 422) {
        // Handle validation errors
        if (error.response?.data?.detail) {
          const detail = error.response.data.detail;
          if (Array.isArray(detail)) {
            const firstError = detail[0];
            if (firstError?.loc) {
              const field = firstError.loc[firstError.loc.length - 1];
              if (field === 'title') {
                if (firstError.msg && firstError.msg.includes('malicious content')) {
                  errorMessage = 'Der Titel enthält nicht erlaubte Inhalte (z.B. HTML-Tags oder Skripte). Bitte verwende nur normalen Text.';
                } else if (firstError.msg && firstError.msg.includes('200 characters')) {
                  errorMessage = 'Der Titel ist zu lang. Bitte verwende maximal 200 Zeichen.';
                } else if (firstError.msg && firstError.msg.includes('required')) {
                  errorMessage = 'Der Titel ist erforderlich.';
                }
              } else if (field === 'description') {
                if (firstError.msg && firstError.msg.includes('malicious content')) {
                  errorMessage = 'Die Beschreibung enthält nicht erlaubte Inhalte (z.B. HTML-Tags oder Skripte). Bitte verwende nur normalen Text.';
                } else if (firstError.msg && firstError.msg.includes('2000 characters')) {
                  errorMessage = 'Die Beschreibung ist zu lang. Bitte verwende maximal 2000 Zeichen.';
                } else if (firstError.msg && firstError.msg.includes('required')) {
                  errorMessage = 'Die Beschreibung ist erforderlich.';
                }
              } else if (field === 'url') {
                errorMessage = 'Bitte gib eine gültige URL ein (beginnt mit http:// oder https://).';
              } else if (field === 'additional_info') {
                if (firstError.msg && firstError.msg.includes('malicious content')) {
                  errorMessage = 'Die zusätzlichen Informationen enthalten nicht erlaubte Inhalte (z.B. HTML-Tags oder Skripte). Bitte verwende nur normalen Text.';
                } else if (firstError.msg && firstError.msg.includes('1000 characters')) {
                  errorMessage = 'Die zusätzlichen Informationen sind zu lang. Bitte verwende maximal 1000 Zeichen.';
                }
              } else if (field === 'report_type') {
                errorMessage = 'Bitte wähle einen gültigen Berichtstyp aus.';
              } else {
                errorMessage = 'Die eingegebenen Daten sind ungültig.';
              }
            } else {
              errorMessage = detail;
            }
          } else {
            errorMessage = detail;
          }
        } else {
          errorMessage = 'Die eingegebenen Daten sind ungültig.';
        }
      } else if (error.response?.status === 429) {
        // Rate limit exceeded
        errorMessage = error.response.data.detail || 'Du hast das Limit für Berichte erreicht. Bitte versuche es später erneut.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    
    // Combine with existing files
    const newFiles = [...files, ...selectedFiles];
    
    // Validate the new file list
    const filesError = validateFiles(newFiles);
    setErrors(prev => ({ ...prev, files: filesError }));
    
    if (filesError) {
      setSnackbar({
        open: true,
        message: filesError,
        severity: 'error'
      });
      // Don't add files if there's an error
      event.target.value = '';
      return;
    }
    
    // Limit to 3 files total
    const finalFiles = newFiles.slice(0, 3);
    setFiles(finalFiles);
    
    // Reset the input value to allow selecting the same file again
    event.target.value = '';
  };

  const removeFile = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
    setTouched(prev => ({ ...prev, files: true }));
    updateValidation('files', newFiles);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Auto-hide tooltip when dialog opens
  useEffect(() => {
    if (showReportDialog) {
      setShowTooltip(false);
    }
  }, [showReportDialog]);

  const handleCloseDialogs = () => {
    setShowReportDialog(false);
    setShowTooltip(false);
    
    // Reset form and validation
    setReportType('');
    setDescription('');
    setUrl('');
    setFiles([]);
    setErrors({
      reportType: '',
      description: '',
      url: '',
      files: ''
    });
    setTouched({
      reportType: false,
      description: false,
      url: false,
      files: false
    });
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <ReportIconContainer showTooltip={showTooltip}>
        <ReportIconOverlay
          onClick={() => {
            setShowReportDialog(true);
            setShowTooltip(false); // Hide tooltip when opening dialog
          }}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          onTouchStart={() => {
            // On mobile, show tooltip briefly then hide it
            setShowTooltip(true);
            setTimeout(() => setShowTooltip(false), 2000);
          }}
        >
          <ReportIcon sx={{ color: 'white', fontSize: '28px' }} />
        </ReportIconOverlay>
        
        {showTooltip && (
          <ReportTooltip>
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
              Verdächtige Aktivität melden
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              Melde Fake-Shops, Phishing-Versuche, Malware oder andere verdächtige Aktivitäten
            </Typography>
          </ReportTooltip>
        )}
      </ReportIconContainer>

      {/* Report Dialog */}
      <Dialog
        open={showReportDialog}
        onClose={handleCloseDialogs}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
          color: 'white',
          textAlign: 'center',
          py: 3
        }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
            Verdächtige Aktivität melden
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
            Hilf anderen, sich vor Cyberbedrohungen zu schützen
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 4 }}>
            {/* Report Type */}
            <FormControl fullWidth required error={!!errors.reportType && touched.reportType}>
              <InputLabel>Berichtstyp *</InputLabel>
              <Select
                value={reportType}
                onChange={(e) => {
                  setReportType(e.target.value);
                  setTouched(prev => ({ ...prev, reportType: true }));
                  updateValidation('reportType', e.target.value);
                }}
                label="Berichtstyp"
              >
                {reportTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {type.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {type.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {errors.reportType && touched.reportType && (
                <FormHelperText>{errors.reportType}</FormHelperText>
              )}
            </FormControl>

            {/* Description */}
            <TextField
              fullWidth
              required
              label="Beschreibung"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setTouched(prev => ({ ...prev, description: true }));
                updateValidation('description', e.target.value);
              }}
              onBlur={() => setTouched(prev => ({ ...prev, description: true }))}
              error={!!errors.description && touched.description}
              helperText={
                errors.description && touched.description 
                  ? errors.description 
                  : `Max. 2000 Zeichen, nur Text erlaubt (keine HTML-Tags) - ${description.length}/2000`
              }
              inputProps={{ maxLength: 2000 }}
              multiline
              rows={4}
            />

            {/* URL */}
            <TextField
              fullWidth
              label="URL (optional)"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                setTouched(prev => ({ ...prev, url: true }));
                updateValidation('url', e.target.value);
              }}
              onBlur={() => setTouched(prev => ({ ...prev, url: true }))}
              error={!!errors.url && touched.url}
              helperText={
                errors.url && touched.url 
                  ? errors.url 
                  : "URL der verdächtigen Website (beginnt mit http:// oder https://)"
              }
              placeholder="https://example.com"
            />

            {/* File Upload */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Screenshots (optional, max. 3)
              </Typography>
              <input
                accept="image/jpeg,image/jpg,image/png,image/webp"
                style={{ display: 'none' }}
                id="file-upload"
                multiple
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  sx={{ mb: 2 }}
                >
                  Dateien auswählen
                </Button>
              </label>
              <Typography 
                variant="caption" 
                display="block" 
                color={errors.files && touched.files ? 'error' : 'text.secondary'}
              >
                {errors.files && touched.files 
                  ? errors.files 
                  : "Erlaubte Formate: JPG, PNG, WebP (max. 10MB pro Datei)"
                }
              </Typography>
              
              {/* File List */}
              {files.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {files.map((file, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        mb: 1,
                        backgroundColor: '#f5f5f5'
                      }}
                    >
                      <Typography variant="body2" noWrap sx={{ flexGrow: 1, mr: 2 }}>
                        {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => removeFile(index)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleCloseDialogs}
            variant="outlined"
            sx={{ 
              borderRadius: '8px',
              px: 3,
              py: 1.5
            }}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleReportSubmit}
            variant="contained"
            sx={{ 
              background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
              borderRadius: '8px',
              px: 3,
              py: 1.5,
              '&:hover': {
                background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
              }
            }}
          >
            Bericht einreichen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Consent Dialog */}
      <Dialog
        open={consentDialogOpen}
        onClose={() => setConsentDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#f5f5f5',
          color: '#333',
          textAlign: 'center',
          py: 2,
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 'normal' }}>
            Einverständniserklärung
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
              Mit deiner Meldung erteilst Du uns deine Einwilligung, dass wir die von dir 
              bekanntgegebenen Daten (Namen, E-Mail-Adresse und Inhalt der Meldung samt Anhänge) 
              zur Bearbeitung deiner Meldung verarbeiten sowie für ein Jahr speichern, um 
              allfällige Nachfragen richtig zuordnen und Entwicklungen im Bereich des 
              Internet-Betrugs dokumentieren zu können. Du kannst deine Einwilligung jederzeit 
              widerrufen, indem Du dich an uns unter den angegebenen Kontaktdaten (
              <a href="https://www.cyberlagebild.de/kontakt" target="_blank" rel="noopener noreferrer">
                https://www.cyberlagebild.de/kontakt
              </a>
              ) wendest.
            </Typography>
            
            <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
              Aus deiner Meldung ergeben sich keinerlei Beratungs-, Rechts- oder 
              Veröffentlichungsansprüche. Bei einer Veröffentlichung werden selbstverständlich 
              sämtliche personenbezogene Daten anonymisiert.
            </Typography>
            
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              backgroundColor: '#f9f9f9', 
              borderRadius: '4px',
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 'normal', color: '#666' }}>
                Hinweis: Durch das Bestätigen stimmst du der Verarbeitung deiner Daten 
                gemäß den oben genannten Bedingungen zu.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={() => setConsentDialogOpen(false)}
            variant="outlined"
            sx={{ 
              borderRadius: '8px',
              px: 3,
              py: 1.5
            }}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleConsentConfirm}
            variant="contained"
            sx={{ 
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              borderRadius: '8px',
              px: 3,
              py: 1.5,
              '&:hover': {
                background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
              }
            }}
          >
            Einverstanden und Bericht einreichen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          sx={{ 
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ReportIconComponent;
